import { formatDepartmentDisplay, isDepartementChampKey } from "@/lib/departementDisplay";
import { normalizePhoneFieldsInDossierPayload } from "@/lib/dossierPhoneNormalize";

export function normalizeAnnonceType(raw) {
  const t = String(raw || "").toLowerCase().trim();
  if (t === "recherche" || t === "achat" || t === "buy") return "recherche";
  if (t === "vente" || t === "offre" || t === "sell") return "vente";
  return t;
}

export function normalizeChampKey(s) {
  return String(s || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ");
}

export const isContactLikeChamp = (nom) => {
  const k = normalizeChampKey(nom);
  return (
    k === "nom du client" ||
    k === "nom du client/prospect" ||
    k === "telephone du client" ||
    k === "telephone du client/prospect" ||
    k === "tel du client" ||
    k === "adresse du client" ||
    k === "adresse client" ||
    k === "departement" ||
    k === "département" ||
    k === "pays" ||
    k === "localisation" ||
    k === "location"
  );
};

export const isClientIdentityChamp = (nom) => {
  const k = normalizeChampKey(nom);
  return (
    k === "nom du client" ||
    k === "nom du client/prospect" ||
    k === "telephone du client" ||
    k === "telephone du client/prospect" ||
    k === "tel du client" ||
    k === "adresse du client" ||
    k === "adresse client"
  );
};

export function initAnnonceEditForm(d) {
  const row = d || {};
  return {
    type: normalizeAnnonceType(row?.type) || "vente",
    secteur: row?.secteur || "",
    produit_id: row?.produit_id || "",
    title: row?.title || row?.titre || "",
    description: row?.description || "",
    location: row?.location || "",
    client_name: row?.client_name || "",
    client_phone: row?.client_phone || "",
    price: row?.price != null ? String(row.price) : "",
    price_min: row?.price_min != null ? String(row.price_min) : "",
    price_max: row?.price_max != null ? String(row.price_max) : "",
    custom_fields:
      row?.custom_fields && typeof row.custom_fields === "object" ? { ...row.custom_fields } : {},
  };
}

function asNullableNumber(v) {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export function buildAnnonceEditPayload(form, { editingDossier, editProduitSelectionne, editDeptChampKey }) {
  const editAnnonceForm = form || {};
  const stripRechercheContactFields = (customFieldsIn) => {
    const cf = customFieldsIn && typeof customFieldsIn === "object" ? { ...customFieldsIn } : {};
    const champs = Array.isArray(editProduitSelectionne?.champs) ? editProduitSelectionne.champs : [];
    for (const c of champs) {
      const nom = c?.nom;
      if (!nom) continue;
      if (c.visible_public === false || isClientIdentityChamp(nom)) {
        delete cf[nom];
      }
    }
    for (const k of Object.keys(cf)) {
      if (isClientIdentityChamp(k)) delete cf[k];
    }
    return cf;
  };
  const syncContactIntoCustomFields = (customFieldsIn) => {
    const cf = customFieldsIn && typeof customFieldsIn === "object" ? { ...customFieldsIn } : {};
    const champs = Array.isArray(editProduitSelectionne?.champs) ? editProduitSelectionne.champs : [];
    const setIfPresent = (nom, value) => {
      if (value == null || String(value).trim() === "") return;
      cf[nom] = value;
    };
    for (const c of champs) {
      const nom = c?.nom;
      if (!nom) continue;
      const k = normalizeChampKey(nom);
      if (k === "nom du client" || k === "nom du client/prospect") setIfPresent(nom, editAnnonceForm.client_name);
      if (k === "telephone du client" || k === "telephone du client/prospect" || k === "tel du client") {
        setIfPresent(nom, editAnnonceForm.client_phone);
      }
      if (k === "localisation" || k === "location") setIfPresent(nom, editAnnonceForm.location);
    }
    return cf;
  };

  const annonceType = normalizeAnnonceType(editAnnonceForm.type) || "vente";
  const rawCustomFields =
    editAnnonceForm.custom_fields && typeof editAnnonceForm.custom_fields === "object"
      ? editAnnonceForm.custom_fields
      : {};
  const payload = {
    type: annonceType,
    secteur: editAnnonceForm.secteur?.trim() || null,
    produit_id: editAnnonceForm.produit_id || null,
    title: editAnnonceForm.title?.trim(),
    description: editAnnonceForm.description?.trim(),
    location: editAnnonceForm.location?.trim(),
    client_name: annonceType === "recherche" ? null : editAnnonceForm.client_name?.trim() || null,
    client_phone: annonceType === "recherche" ? null : editAnnonceForm.client_phone?.trim() || null,
    custom_fields:
      annonceType === "recherche"
        ? stripRechercheContactFields(rawCustomFields)
        : syncContactIntoCustomFields(rawCustomFields),
  };
  const out =
    annonceType === "recherche" ? payload : normalizePhoneFieldsInDossierPayload(payload);

  if (out.type === "recherche") {
    out.price_min = asNullableNumber(editAnnonceForm.price_min);
    out.price_max = asNullableNumber(editAnnonceForm.price_max);
    out.price = out.price_max ?? null;
    out.recherche_criteres_requis = Array.isArray(editingDossier?.recherche_criteres_requis)
      ? editingDossier.recherche_criteres_requis
      : [];
    if (editDeptChampKey) {
      const deptRaw = String(out.custom_fields?.[editDeptChampKey] ?? "").trim();
      const deptDisplay = formatDepartmentDisplay(deptRaw) || deptRaw;
      if (deptDisplay) {
        out.custom_fields = { ...out.custom_fields, [editDeptChampKey]: deptDisplay };
        out.location = deptDisplay;
      } else {
        delete out.custom_fields[editDeptChampKey];
        out.location = null;
      }
    } else {
      out.location = null;
    }
  } else {
    out.price = asNullableNumber(editAnnonceForm.price);
    out.price_min = null;
    out.price_max = null;
    out.recherche_criteres_requis = [];
  }
  return out;
}

export function resolveEditDeptChampKey(editProduitSelectionne, annonceType) {
  const champs = Array.isArray(editProduitSelectionne?.champs) ? editProduitSelectionne.champs : [];
  const t = normalizeAnnonceType(annonceType);
  const hit = champs.find((c) => {
    if (!c?.nom) return false;
    const ca = c.cible_annonces;
    const cibleOk = ca == null || ca === "tous" || ca === t;
    if (!cibleOk) return false;
    return isDepartementChampKey(c.nom);
  });
  return hit?.nom ? String(hit.nom) : null;
}

export function filterEditChampsDynamiques(editProduitSelectionne, form, editDeptChampKey) {
  const champs = Array.isArray(editProduitSelectionne?.champs) ? editProduitSelectionne.champs : [];
  const t = normalizeAnnonceType(form?.type);
  return champs.filter((c) => {
    if (!c?.nom) return false;
    if (editDeptChampKey && String(c.nom) === editDeptChampKey) return false;
    if (isContactLikeChamp(c.nom)) return false;
    if (t === "recherche" && c.visible_public === false) return false;
    const ca = c.cible_annonces;
    if (ca == null || ca === "tous") return true;
    return ca === t;
  });
}

export function editChampsMasquesRechercheList(editProduitSelectionne, annonceType) {
  if (normalizeAnnonceType(annonceType) !== "recherche") return [];
  const champs = Array.isArray(editProduitSelectionne?.champs) ? editProduitSelectionne.champs : [];
  return champs
    .filter((c) => {
      if (!c?.nom) return false;
      if (c.visible_public !== false) return false;
      const ca = c.cible_annonces;
      return ca == null || ca === "tous" || ca === "recherche";
    })
    .map((c) => String(c.nom));
}
